import { ButtonHTMLAttributes, forwardRef, Ref } from "react";
import styled from "@emotion/styled";
import { smoothTransition } from "styles/animations";
import { Flex, Text } from "styles/utility-components";

import UIcon, { IconProps } from "lib/icons/untitled_ui/UIcon";

type QuickActionButtonProps = {
  text: string;
  icon: IconProps["icon"];
} & ButtonHTMLAttributes<HTMLButtonElement>;

const QuickActionButton = forwardRef(
  ({ text, icon, onClick, ...rest }: QuickActionButtonProps, ref: Ref<HTMLButtonElement>) => {
    return (
      <Button ref={ref} onClick={onClick} {...rest}>
        <Flex alignItems="center" gap={12} height="100%">
          <IconContainer>
            <UIcon icon={icon} size={16} color="sysPrimaryDefault" />
          </IconContainer>
          <Text>{text}</Text>
        </Flex>
      </Button>
    );
  },
);

const Button = styled.button(({ theme }) => ({
  height: 56,
  width: "fit-content",
  backgroundColor: theme.colors.sysBackgroundDefault,
  cursor: "pointer",
  borderRadius: 8,
  border: `1px solid ${theme.colors.sysBorderDecorative}`,
  padding: 12,
  "&:hover": {
    backgroundColor: theme.colors.sysPrimarySubtle,
    borderColor: theme.colors.sysPrimaryHover,
    transition: smoothTransition,
    [`${IconContainer}`]: {
      backgroundColor: theme.colors.sysPrimarySubtleAlt,
    },
  },
  "&:active": {
    borderColor: theme.colors.sysPrimaryPressed,
    backgroundColor: theme.colors.sysPrimarySubtleAlt,
  },
}));

const IconContainer = styled(Flex)(({ theme }) => ({
  padding: 8,
  backgroundColor: theme.colors.sysPrimarySubtle,
  borderRadius: 4,
}));

QuickActionButton.displayName = Button.displayName;

export default QuickActionButton;
